/**
 * Date Utilities
 *
 * Date and time manipulation utilities using dayjs.
 */

import dayjs, { type Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(weekday);
dayjs.extend(weekOfYear);

// ===== TYPES =====

export interface DateRange {
  start: Date;
  end: Date;
}

export interface CalendarDay {
  date: Date;
  dayName: string;
  monthName: string;
  weekString: string;
  isToday: boolean;
  isSelected: boolean;
  millisecondCount: number;
}

export type CalendarWeek = CalendarDay[];
export type CalendarMonth = Record<string, CalendarDay[][]>;

// ===== CONSTANTS =====

export const DATE_FORMATS = {
  ISO: "YYYY-MM-DD",
  ISO_DATETIME: "YYYY-MM-DDTHH:mm:ss",
  ISO_DATETIME_WITH_TZ: "YYYY-MM-DDTHH:mm:ssZ",
  DISPLAY_DATE: "MMM DD, YYYY",
  DISPLAY_DATETIME: "MMM DD, YYYY HH:mm",
  SHORT_DATE: "MM/DD/YYYY",
  LONG_DATE: "MMMM DD, YYYY",
  TIME_12H: "hh:mm A",
  TIME_24H: "HH:mm",
  SHORT_DATE_V2: "DD-MM-YYYY",
  SORT_DATE_SLASH: "DD/MM/YYYY",
} as const;

export const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

// ===== BASIC DATE OPERATIONS =====

/**
 * Get current date
 */
export function now(): Date {
  return new Date();
}

/**
 * Get current date as dayjs object
 */
export function nowDayjs(): Dayjs {
  return dayjs();
}

/**
 * Create a date from various inputs
 */
export function createDate(input?: string | number | Date | Dayjs): Date {
  return dayjs(input).toDate();
}

/**
 * Create a dayjs object from various inputs
 */
export function createDayjs(input?: string | number | Date | Dayjs): Dayjs {
  return dayjs(input);
}

/**
 * Check if a value is a valid date
 */
export function isValidDate(date: unknown): date is Date {
  return date instanceof Date && !Number.isNaN(date.getTime());
}

/**
 * Check if a dayjs object is valid
 */
export function isValidDayjs(date: Dayjs): boolean {
  return date.isValid();
}

// ===== FORMATTING =====

/**
 * Format a date using the specified format
 */
export function formatDate(
  date: string | number | Date | Dayjs,
  format: string = DATE_FORMATS.DISPLAY_DATE,
): string {
  return dayjs(date).format(format);
}

/**
 * Format a date for display
 */
export function formatDisplayDate(date: string | number | Date | Dayjs): string {
  return formatDate(date, DATE_FORMATS.DISPLAY_DATE);
}

/**
 * Format a date and time for display
 */
export function formatDisplayDateTime(date: string | number | Date | Dayjs): string {
  return formatDate(date, DATE_FORMATS.DISPLAY_DATETIME);
}

/**
 * Format a date as ISO string
 */
export function formatISO(date: string | number | Date | Dayjs): string {
  return dayjs(date).toISOString();
}

/**
 * Format time in 12-hour format
 */
export function formatTime12h(date: string | number | Date | Dayjs): string {
  return formatDate(date, DATE_FORMATS.TIME_12H);
}

/**
 * Format time in 24-hour format
 */
export function formatTime24h(date: string | number | Date | Dayjs): string {
  return formatDate(date, DATE_FORMATS.TIME_24H);
}

/**
 * Get relative time (e.g., "2 hours ago", "in 3 days")
 */
export function getRelativeTime(date: string | number | Date | Dayjs): string {
  return dayjs(date).fromNow();
}

// ===== PARSING =====

/**
 * Parse a date string with a specific format
 */
export function parseDate(dateString: string, format: string = DATE_FORMATS.ISO): Date | null {
  const parsed = dayjs(dateString, format);
  return parsed.isValid() ? parsed.toDate() : null;
}

/**
 * Parse an ISO date string
 */
export function parseISO(isoString: string): Date | null {
  return parseDate(isoString, DATE_FORMATS.ISO_DATETIME_WITH_TZ);
}

// ===== DATE ARITHMETIC =====

/**
 * Add time to a date
 */
export function addTime(
  date: string | number | Date | Dayjs,
  amount: number,
  unit: dayjs.ManipulateType,
): Date {
  return dayjs(date).add(amount, unit).toDate();
}

/**
 * Subtract time from a date
 */
export function subtractTime(
  date: string | number | Date | Dayjs,
  amount: number,
  unit: dayjs.ManipulateType,
): Date {
  return dayjs(date).subtract(amount, unit).toDate();
}

/**
 * Add days to a date
 */
export function addDays(date: string | number | Date | Dayjs, days: number): Date {
  return addTime(date, days, "day");
}

/**
 * Add weeks to a date
 */
export function addWeeks(date: string | number | Date | Dayjs, weeks: number): Date {
  return addTime(date, weeks, "week");
}

/**
 * Add months to a date
 */
export function addMonths(date: string | number | Date | Dayjs, months: number): Date {
  return addTime(date, months, "month");
}

/**
 * Add years to a date
 */
export function addYears(date: string | number | Date | Dayjs, years: number): Date {
  return addTime(date, years, "year");
}

// ===== DATE COMPARISON =====

/**
 * Check if two dates are the same
 */
export function isSameDate(
  date1: string | number | Date | Dayjs,
  date2: string | number | Date | Dayjs,
  unit: dayjs.OpUnitType = "day",
): boolean {
  return dayjs(date1).isSame(dayjs(date2), unit);
}

/**
 * Check if date1 is before date2
 */
export function isBefore(
  date1: string | number | Date | Dayjs,
  date2: string | number | Date | Dayjs,
  unit?: dayjs.OpUnitType,
): boolean {
  return dayjs(date1).isBefore(dayjs(date2), unit);
}

/**
 * Check if date1 is after date2
 */
export function isAfter(
  date1: string | number | Date | Dayjs,
  date2: string | number | Date | Dayjs,
  unit?: dayjs.OpUnitType,
): boolean {
  return dayjs(date1).isAfter(dayjs(date2), unit);
}

/**
 * Check if a date is today
 */
export function isToday(date: string | number | Date | Dayjs): boolean {
  return isSameDate(date, now(), "day");
}

/**
 * Check if a date is yesterday
 */
export function isYesterday(date: string | number | Date | Dayjs): boolean {
  return isSameDate(date, subtractTime(now(), 1, "day"), "day");
}

/**
 * Check if a date is tomorrow
 */
export function isTomorrow(date: string | number | Date | Dayjs): boolean {
  return isSameDate(date, addTime(now(), 1, "day"), "day");
}

/**
 * Check if a date is in the current week
 */
export function isThisWeek(date: string | number | Date | Dayjs): boolean {
  return isSameDate(date, now(), "week");
}

/**
 * Check if a date is in the current month
 */
export function isThisMonth(date: string | number | Date | Dayjs): boolean {
  return isSameDate(date, now(), "month");
}

/**
 * Check if a date is in the current year
 */
export function isThisYear(date: string | number | Date | Dayjs): boolean {
  return isSameDate(date, now(), "year");
}

// ===== DATE RANGES =====

/**
 * Get the difference between two dates
 */
export function getDifference(
  date1: string | number | Date | Dayjs,
  date2: string | number | Date | Dayjs,
  unit: dayjs.QUnitType = "day",
): number {
  return dayjs(date1).diff(dayjs(date2), unit);
}

/**
 * Check if a date is within a range
 */
export function isWithinRange(
  date: string | number | Date | Dayjs,
  start: string | number | Date | Dayjs,
  end: string | number | Date | Dayjs,
): boolean {
  const d = dayjs(date);
  return d.isSameOrAfter(dayjs(start)) && d.isSameOrBefore(dayjs(end));
}

/**
 * Get the start of a time period
 */
export function startOf(date: string | number | Date | Dayjs, unit: dayjs.OpUnitType): Date {
  return dayjs(date).startOf(unit).toDate();
}

/**
 * Get the end of a time period
 */
export function endOf(date: string | number | Date | Dayjs, unit: dayjs.OpUnitType): Date {
  return dayjs(date).endOf(unit).toDate();
}

/**
 * Calculate age from a date of birth.
 * Returns a string in "Xyr Xmo Xd" format
 * Examples: "80yr 6mo 8d", "2yr 3mo", "5mo 10d", "15d"
 */
export function calculateAge(dateOfBirth: Dayjs | Date | string | null): string {
  if (!dateOfBirth) return "";

  const birthDate = dayjs(dateOfBirth);
  if (!birthDate.isValid()) return "";

  const today = dayjs();

  const years = today.diff(birthDate, "year");
  const birthDatePlusYears = birthDate.add(years, "year");
  const months = today.diff(birthDatePlusYears, "month");
  const birthDatePlusYearsMonths = birthDatePlusYears.add(months, "month");
  const days = today.diff(birthDatePlusYearsMonths, "day");

  const parts: string[] = [];
  if (years > 0) parts.push(`${years}yr`);
  if (months > 0) parts.push(`${months}mo`);
  if (days > 0) parts.push(`${days}d`);

  return parts.length > 0 ? parts.join(" ") : "0d";
}

// ===== TIMEZONE UTILITIES =====

/**
 * Convert date to UTC
 */
export function toUTC(date: string | number | Date | Dayjs): Date {
  return dayjs(date).utc().toDate();
}

/**
 * Convert date to specific timezone
 */
export function toTimezone(date: string | number | Date | Dayjs, timezone: string): Date {
  return dayjs(date).tz(timezone).toDate();
}

/**
 * Get the current timezone
 */
export function getCurrentTimezone(): string {
  return dayjs.tz.guess();
}

// ===== LEGACY COMPATIBILITY =====

/**
 * Legacy DateManager compatibility
 * @deprecated Use the new date utilities instead
 */
export const DateManager = {
  formatDate,
  parseDate,
  addDays,
  isToday,
  now,
} as const;
